import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import contactDefault from "../../data/contact.json";
import { getContactPage } from "../../services/data.service";
import { mediaUrl } from "../../services/assetUrl";
import Reveal from "../home/Reveal";
import PageBanner from "../../components/PageBanner";
import "../home/homeRedesign.css"; // shared primitives: .btn-ed, .eyebrow-ed, .reveal-ed
import "./contact.css";
import LeadForm from "./LeadForm";
import QueryForm from "./QueryForm";

const telHref = (num) => `tel:${num.replace(/[^\d+]/g, "")}`;

const IconPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);
const IconMail = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);
const IconPin = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ContactMain = () => {
  const { search } = useLocation();
  const enquireOnly = new URLSearchParams(search).get("enquire") === "1";

  // Backend-driven content with bundled JSON as the instant fallback (same
  // pattern as Careers / Home). The two forms receive the resolved content prop.
  const [contact, setContact] = useState(contactDefault);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getContactPage();
        const d = res?.data?.data; // SERVICE + wrapped doc → res.data.data
        if (active && d) setContact(d);
      } catch (e) {
        /* keep bundled fallback on 404 / error */
      }
    })();
    return () => { active = false; };
  }, []);

  const gt = contact.getInTouch;

  // Two-tone title: accent the last word in orange (e.g. Contact "Us").
  const titleParts = contact.title.trim().split(" ");
  const titleLast = titleParts.pop();
  const titleWords = titleParts;

  return (
    <div className="react-wrapper">
      <div className="react-wrapper-inner">
        {/* ---- Intro banner (shared PageBanner) ---- */}
        <PageBanner
          title={contact.title}
          eyebrow={contact.eyebrow}
          subtitle={contact.subtitle}
          image={contact.bannerImage ? mediaUrl(contact.bannerImage) : undefined}
        />

        {/* ---- Lead capture form (hidden in enquire-only mode) ---- */}
        {!enquireOnly && (
          <section className="contact-ed__lead">
            <div className="container">
              <Reveal><LeadForm contact={contact} /></Reveal>
            </div>
          </section>
        )}

        {/* ---- Get In Touch ---- */}
        <section
          id="get-in-touch"
          className={`contact-ed__touch ${enquireOnly ? "contact-ed__section" : "contact-ed__section--alt"}`}
        >
          <div className="container">
            <div className="contact-ed__touch-grid">
              {/* Contact info */}
              <Reveal>
                <div className="con-info">
                  <span className="eyebrow-ed">{gt.eyebrow}</span>
                  <h2 className="heading-ed">{gt.heading}</h2>
                  <p className="con-info__intro">{gt.intro}</p>

                  <ul className="con-info__list">
                    {gt.phoneGroups.map((grp) => (
                      <li className="con-info__item" key={grp.label}>
                        <span className="con-info__icon"><IconPhone /></span>
                        <div>
                          <p className="con-info__label">{grp.label}</p>
                          <div className="con-info__value">
                            {grp.numbers.map((num, i) => (
                              <React.Fragment key={num}>
                                {i > 0 && <span className="con-sep">·</span>}
                                <a href={telHref(num)}>{num}</a>
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      </li>
                    ))}
                    <li className="con-info__item">
                      <span className="con-info__icon"><IconMail /></span>
                      <div>
                        <p className="con-info__label">{gt.email.label}</p>
                        <div className="con-info__value">
                          <a href={`mailto:${gt.email.value}`}>{gt.email.value}</a>
                        </div>
                      </div>
                    </li>
                  </ul>

                  <div className="con-address">
                    <h3 className="con-address__title">{gt.address.label}</h3>
                    <address className="con-address__lines">
                      {gt.address.lines.map((ln) => (
                        <React.Fragment key={ln}>{ln}<br /></React.Fragment>
                      ))}
                    </address>
                    <a
                      className="btn-ed btn-ed--ghost con-maps-btn"
                      href={gt.mapUrl} target="_blank" rel="noopener noreferrer"
                    >
                      <IconPin /> View On Google Maps
                    </a>
                  </div>
                </div>
              </Reveal>

              {/* Query form */}
              <Reveal><QueryForm contact={contact} /></Reveal>
            </div>
          </div>
        </section>

        {/* ---- Map ---- */}
        <section className="contact-ed__map">
          <div className="container">
            <Reveal>
              <div className="contact-ed__map-frame">
                <iframe
                  title="NCET campus location map"
                  src={gt.mapEmbed}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactMain;
